import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Receipt as ReceiptIcon, Star } from "lucide-react";
import api, { PLATFORM_NAME, errMsg, money, openCheckout } from "@/lib/api";
import { useRiderScope } from "@/lib/riderScope";
import { clock } from "@/lib/lifecycle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const TIP_PERCENTS = [18, 20, 25];

export default function TripReceipt() {
  const { companyId, token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [tipPercent, setTipPercent] = useState(null);
  const [customTip, setCustomTip] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [tipUrl, setTipUrl] = useState("");
  const handle = data?.company?.handle || data?.company?.id || companyId;
  useRiderScope(handle, `receipt/${token}`);

  const load = useCallback(
    () => api.get(`/public/track/${token}/receipt`).then(({ data }) => setData(data)).catch((e) => setError(errMsg(e))),
    [token]
  );
  useEffect(() => { load(); }, [load]);

  // Back from the gratuity card form — confirm with the processor, then show it on the receipt.
  useEffect(() => {
    const payToken = params.get("tip_token");
    if (!payToken) return undefined;
    const check = () => api.get(`/public/pay/${payToken}/status`).then(() => load()).catch(() => {});
    check();
    const timer = setTimeout(check, 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, token]);

  if (error && !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900 p-6">
        <p data-testid="receipt-error" className="text-sm text-red-400">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900">
        <Loader2 className="h-5 w-5 animate-spin text-gold" />
      </div>
    );
  }

  const c = data.company;
  const tipAmount = tipPercent
    ? Math.round((data.total * tipPercent) / 100 * 100) / 100
    : Number(customTip || 0);

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data: res } = await api.post(`/public/track/${token}/feedback`, {
        rating, comment, tip: tipAmount,
      });
      setDone(res.thanks);
      if (res.charge_tip && tipAmount > 0) {
        // The gratuity is charged to the card, not quietly added to an invoice.
        const { data: pay } = await api.post(`/public/track/${token}/tip-checkout`, { tip: tipAmount });
        if (!openCheckout(pay.checkout_url, setTipUrl)) setTipUrl(pay.checkout_url);
      }
      load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="receipt-page" className="min-h-screen bg-ink-900 px-4 py-10 text-slate-200">
      <div className="mx-auto max-w-xl space-y-6 rise">
        <div className="rounded-xl border border-ink-600 bg-ink-800 p-6"
          style={{ borderTop: `3px solid ${c.branding?.primary_color || "#D4AF37"}` }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">{c.name}</p>
              <h1 className="mt-2 flex items-center gap-2 text-2xl font-bold text-slate-50">
                <ReceiptIcon className="h-5 w-5 text-gold" /> Trip #{data.number}
              </h1>
              <p className="mt-1 text-xs text-slate-500">
                {data.trip_date} · {data.pickup_time} · {data.chauffeur_name || "your chauffeur"}
              </p>
            </div>
            {c.logo_url && <img src={c.logo_url} alt={`${c.name} logo`} className="h-12 w-auto object-contain" />}
          </div>
          <p className="mt-4 text-xs text-slate-400">{data.pickup?.address} → {data.dropoff?.address}</p>
          {(data.recorded_minutes >= 1 || data.recorded_miles >= 0.1) ? (
            <p data-testid="receipt-recorded" className="mt-2 font-mono text-[11px] text-slate-500">
              {clock(data.recorded_minutes)} on the road · {(data.recorded_miles || 0).toFixed(1)} mi
            </p>
          ) : null}
        </div>

        <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
          {(data.lines || []).map((l, i) => (
            <div key={`${l.label}-${i}`} data-testid={`receipt-line-${i}`} className="flex justify-between border-b border-ink-600 py-2 text-sm">
              <span className="text-slate-400">{l.label}</span>
              <span className="text-slate-100">{money(l.amount)}</span>
            </div>
          ))}
          {data.tip > 0 && (
            <div className="flex justify-between border-b border-ink-600 py-2 text-sm">
              <span className="text-slate-400">Gratuity</span>
              <span className="text-slate-100">{money(data.tip)}</span>
            </div>
          )}
          <div className="mt-3 flex justify-between text-base">
            <span className="text-slate-300">Total</span>
            <span data-testid="receipt-total" className="font-display font-semibold text-slate-50">
              {money(data.grand_total ?? data.total)}
            </span>
          </div>
          <div className="mt-1 flex justify-between text-sm">
            <span className="text-slate-500">Paid</span>
            <span className="text-slate-400">{money(data.amount_paid)}</span>
          </div>
          <div className="mt-1 flex justify-between text-sm">
            <span className="text-slate-500">Balance due</span>
            <span data-testid="receipt-balance" className={data.balance_due > 0 ? "text-gold" : "text-emerald-400"}>
              {money(data.balance_due)}
            </span>
          </div>
          {data.invoice_number && (
            <p className="mt-3 font-mono text-[11px] text-slate-500">Invoice #{data.invoice_number}</p>
          )}
          {data.balance_due > 0 && (
            <p data-testid="receipt-balance-note" className="mt-3 text-xs text-slate-400">
              {c.name} will email you a secure payment link for the remaining balance — nothing is charged to your
              card automatically.
            </p>
          )}
        </div>

        {data.rating ? (
          <div data-testid="receipt-rated" className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center">
            <CheckCircle2 className="mx-auto h-6 w-6 text-emerald-400" />
            <p className="mt-3 text-sm text-slate-200">
              You rated this trip {data.rating} of 5{data.tip > 0 ? ` and tipped ${money(data.tip)}` : ""}. Thank you.
            </p>
          </div>
        ) : (
          <div className="space-y-5 rounded-xl border border-ink-600 bg-ink-800 p-6">
            <p className="font-mono text-[11px] uppercase tracking-widest text-gold">How was your ride?</p>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`rate-${n}`} onClick={() => setRating(n)} aria-label={`${n} star`}
                  className="transition-transform hover:scale-110">
                  <Star className={`h-8 w-8 ${n <= rating ? "fill-gold text-gold" : "text-ink-600"}`} />
                </button>
              ))}
            </div>
            <Textarea data-testid="rate-comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Anything you'd like the operator to know?"
              className="border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Add a gratuity</p>
              <p className="mt-1 text-xs text-slate-500">100% of your tip goes to your chauffeur.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {TIP_PERCENTS.map((p) => (
                  <button key={p} data-testid={`tip-${p}`} onClick={() => { setTipPercent(p); setCustomTip(""); }}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                      tipPercent === p ? "border-gold/50 bg-gold/10 text-gold" : "border-ink-600 text-slate-300 hover:border-gold/30"
                    }`}>
                    {p}% · {money((data.total * p) / 100)}
                  </button>
                ))}
                <Input data-testid="tip-custom" type="number" min="0" step="1" value={customTip}
                  onChange={(e) => { setCustomTip(e.target.value); setTipPercent(null); }}
                  placeholder="Other"
                  className="h-10 w-24 border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
              </div>
            </div>
            {error && <p data-testid="receipt-submit-error" className="text-sm text-red-400">{error}</p>}
            <Button data-testid="receipt-submit" disabled={busy || rating === 0} onClick={submit}
              className="h-14 w-full bg-gold text-base text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                : tipAmount > 0 ? `Submit rating and pay ${money(tipAmount)} tip` : "Submit rating"}
            </Button>
            {tipUrl && (
              <a data-testid="receipt-tip-link" href={tipUrl} target="_blank" rel="noopener noreferrer"
                className="block rounded-xl border border-gold/40 bg-gold/10 p-4 text-center text-sm font-semibold text-gold">
                Open the secure card form to add your {money(tipAmount)} gratuity →
              </a>
            )}
            {done && <p data-testid="receipt-thanks" className="text-sm text-emerald-400">{done}</p>}
          </div>
        )}

        {c.show_powered_by && (
          <p className="text-center font-mono text-[11px] text-slate-600">Powered by {PLATFORM_NAME}</p>
        )}
      </div>
    </div>
  );
}
